import React, { useEffect } from "react";
import { useLocation } from "react-router";
import { StatusIndicator } from "./StatusIndicator";
import {
  useNotificationStore,
  NotificationVariant,
} from "stores/useNotificationStore";
import {
  useConnectionStore,
  ConnectionState,
  selectConnectionState,
} from "components/canvas/stores/useConnectionStore";
import { useAuth } from "contexts/AuthProvider";
import { isPublicAuthPage } from "@/constants/authPaths";

// ---------------------------------------------------------------------------
// Variant → StatusIndicator props mapping
// ---------------------------------------------------------------------------

function getColorClass(variant: NotificationVariant): string {
  switch (variant) {
    case "success":
      return "indicator-color-success";
    case "error":
      return "indicator-color-error";
    case "warning":
      return "indicator-color-warning";
    case "default":
      return "indicator-color-default";
  }
}

// The spinner means work is still running, which is a per-notification fact
// (`progress`), never a variant: a warning is usually a finished outcome the
// caller is reporting, and spinning at the reader implies it can still change.
// Severity rides on the variant's color, as it already does for error.
function getIcon(
  variant: NotificationVariant,
  hasProgress: boolean,
): React.ReactNode {
  if (hasProgress) return <div className="indicator-spinner" />;
  switch (variant) {
    case "success":
      return <div className="indicator-check" />;
    case "error":
    case "warning":
    case "default":
      return <div className="indicator-dot" />;
  }
}

function getBehavior(
  variant: NotificationVariant,
  hasProgress: boolean,
): "transient" | "persistent" {
  if (hasProgress) return "persistent";
  // All variants are transient (errors get a longer holdMs)
  return "transient";
}

function getHoldMs(variant: NotificationVariant, hasProgress: boolean): number {
  if (hasProgress) return 2500;
  switch (variant) {
    case "error":
      return 4000;
    case "warning":
      return 4000;
    default:
      return 2500;
  }
}

function getAriaLive(variant: NotificationVariant): "polite" | "assertive" {
  return variant === "error" ? "assertive" : "polite";
}

// ---------------------------------------------------------------------------
// CanvasStatusIndicator — Renders inside #canvas-container
// ---------------------------------------------------------------------------

export const CanvasStatusIndicator: React.FC = () => {
  const current = useNotificationStore((s) => s.current);
  const dismiss = useNotificationStore((s) => s.dismiss);
  const connectionState = useConnectionStore(selectConnectionState);

  // Dismiss on unmount (navigation away from canvas)
  useEffect(() => {
    return () => {
      dismiss();
    };
  }, [dismiss]);

  if (!current) return null;

  const hasProgress = current.progress !== undefined;
  const isConnectionVisible = connectionState !== ConnectionState.Connected;
  const positionClass = isConnectionVisible
    ? "status-indicator-canvas-position"
    : "status-indicator-canvas-position alone";

  // Auto-add Dismiss action for errors without an explicit action
  const action =
    current.variant === "error" && !current.action
      ? { label: "Dismiss", onClick: dismiss }
      : current.action;

  return (
    <div className={positionClass}>
      <StatusIndicator
        key={current.id}
        id={current.id}
        icon={getIcon(current.variant, hasProgress)}
        message={current.message}
        colorClass={getColorClass(current.variant)}
        behavior={getBehavior(current.variant, hasProgress)}
        holdMs={getHoldMs(current.variant, hasProgress)}
        action={action}
        progress={current.progress}
        onDismissed={dismiss}
        ariaLive={getAriaLive(current.variant)}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// GlobalStatusIndicator — Renders on non-canvas pages (fixed position)
// ---------------------------------------------------------------------------

export const GlobalStatusIndicator: React.FC = () => {
  const current = useNotificationStore((s) => s.current);
  const dismiss = useNotificationStore((s) => s.dismiss);
  const location = useLocation();
  const { authenticated } = useAuth();

  // Don't render on canvas routes (CanvasStatusIndicator handles those)
  const isCanvasRoute = location.pathname.startsWith("/canvas/");
  if (!authenticated || isPublicAuthPage(location.pathname) || isCanvasRoute)
    return null;
  if (!current) return null;

  const hasProgress = current.progress !== undefined;

  const action =
    current.variant === "error" && !current.action
      ? { label: "Dismiss", onClick: dismiss }
      : current.action;

  return (
    <div className="status-indicator-global-position">
      <StatusIndicator
        key={current.id}
        id={current.id}
        icon={getIcon(current.variant, hasProgress)}
        message={current.message}
        colorClass={getColorClass(current.variant)}
        behavior={getBehavior(current.variant, hasProgress)}
        holdMs={getHoldMs(current.variant, hasProgress)}
        action={action}
        progress={current.progress}
        onDismissed={dismiss}
        ariaLive={getAriaLive(current.variant)}
      />
    </div>
  );
};
